/** Context provider for user authentication */
import { Preferences } from '@capacitor/preferences';
import React from 'react';
import { useAuth } from '../../hooks/auth/useAuth';
import { UserRole } from '../../model/user.model';
import { AuthState, setAuthed, setError, setLoading, setUnAuthed, useAuthReducer } from '../../store/auth.store';

type AuthContextProps =
  | {
      state: AuthState;
      isAuthorized: () => boolean;
      signUp: (email: string, password: string, displayName: string) => void;
      signInWithEmailAndPassword: (email: string, password: string) => void;
      signInWithGoogle: () => void;
      signInWithFacebook: () => void;
      signInWithTwitter: () => void;
      signOut: () => void;
      resetPassword: (email: string) => void;
      confirmPasswordReset: (confirmationCode: string, newPassword: string) => Promise<boolean>;
      clearError: () => void;
    }
  | undefined;
const authContext = React.createContext<AuthContextProps>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [authState, dispatch] = useAuthReducer();

  React.useEffect(() => {
    useAuth.onAuthChanged(async (user) => {
      if (user) {
        const role = await useAuth.getRole();
        dispatch(setAuthed({ user, role }));
      } else {
        dispatch(setUnAuthed());
      }
    });
  }, [dispatch]);

  function isAuthorized(): boolean {
    const role = authState.userState?.role;
    return !!(role && (role === UserRole.ADMIN || role === UserRole.CLIENT || role === UserRole.ORYXADMIN));
  }

  // Wrap sign-up and sign-in with setting loading and error states
  const tryTo = async <T,>(operation: () => Promise<T>): Promise<T> => {
    try {
      dispatch(setLoading());
      const result = await operation();
      return result;
    } catch (error: any) {
      dispatch(setError(error.message));
      return error;
    }
  };

  function signUp(email: string, password: string, displayName: string) {
    tryTo(async () => {
      await useAuth.createUserWithEmailAndPassword(email, password);
      const user = useAuth.currentUser();
      await user?.sendEmailVerification();
      user?.updateProfile({ displayName });
    });
  }

  function signInWithEmailAndPassword(email: string, password: string) {
    tryTo(async () => useAuth.signInWithEmailAndPassword(email, password));
  }
  // show alert with the custom claims of the user when the user is logged in
  function signInWithGoogle() {
    tryTo(useAuth.signInWithGoogle).then(() => {
      useAuth
        .currentUser()
        ?.getIdTokenResult()
        .then((idTokenResult) => {
          window.alert(idTokenResult.claims.role);
        });
    });
  }

  function signInWithFacebook() {
    tryTo(useAuth.signInWithFacebook);
  }

  function signInWithTwitter() {
    tryTo(useAuth.signInWithTwitter);
  }

  function signOut() {
    tryTo(useAuth.signOut).then(() => {
      // Clear local storage on sign-out.
      Preferences.clear().then(() => {
        console.log('Local storage cleared.');
      });
    });
  }

  function resetPassword(email: string) {
    tryTo(async () => useAuth.resetPassword(email));
  }

  function confirmPasswordReset(confirmationCode: string, newPassword: string): Promise<boolean> {
    return tryTo(async () =>
      useAuth.confirmPasswordReset(confirmationCode, newPassword).then((result) => {
        return result;
      }),
    );
  }

  function clearError() {
    dispatch(setError(''));
  }

  const value: AuthContextProps = {
    state: authState,
    isAuthorized,
    signUp,
    signInWithEmailAndPassword,
    signInWithGoogle,
    signInWithFacebook,
    signInWithTwitter,
    signOut,
    resetPassword,
    confirmPasswordReset,
    clearError,
  };

  return <authContext.Provider value={value}>{children}</authContext.Provider>;
};

export function useAuthContext() {
  const context = React.useContext(authContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
